
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useTranslation } from "@/hooks/useTranslation";
import { UsernameDialog } from "./UsernameDialog";

export function AuthCallback() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const hasProcessed = useRef(false);
  const [searchParams] = useSearchParams();
  const [showUsernameDialog, setShowUsernameDialog] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    // Only process the callback once to prevent multiple redirects
    if (hasProcessed.current) return;
    
    const handleAuthCallback = async () => {
      try {
        hasProcessed.current = true;
        console.log("Processing auth callback with params:", Object.fromEntries(searchParams.entries()));
        
        // Check for error parameters in the URL
        const errorDescription = searchParams.get("error_description");
        if (errorDescription) {
          throw new Error(errorDescription);
        }
        
        // Get the auth flow type
        const type = searchParams.get("type") || 'unknown';
        console.log("Auth callback type:", type);
        
        // Get the current session (this automatically handles the OTP/magic link verification)
        const { data, error } = await supabase.auth.getSession();
        
        if (error) {
          throw error;
        }
        
        if (data.session) {
          console.log("Session established successfully:", data.session.user);
          
          // Set user ID for the username dialog
          setUserId(data.session.user.id);
          
          // Check if a username exists for this user
          const { data: profileData, error: profileError } = await supabase
            .from('profiles')
            .select('username, display_name')
            .eq('id', data.session.user.id)
            .single();
            
          if (profileError) {
            // If the error is not "no rows returned", then log it
            if (profileError.code !== 'PGRST116') {
              console.error("Error checking profile:", profileError);
            }
            
            // Show the username dialog regardless of the type of error
            // This ensures we create a username even if the profile lookup fails
            setShowUsernameDialog(true);
            setIsLoading(false);
            return;
          }
          
          // Determine if this is an OAuth login (Google, etc.)
          const provider = data.session.user.app_metadata.provider;
          const isOAuthLogin = type === 'oauth' || 
                              provider === 'google' || 
                              provider === 'github' || 
                              provider === 'facebook';
                              
          console.log("Is OAuth login:", isOAuthLogin, "Provider:", provider);
          
          // If the user doesn't have a username or display_name
          // Note: Using optional chaining to safely check if these properties exist
          if (!profileData?.username || !profileData?.display_name) {
            console.log("User doesn't have a username or display_name, showing dialog");
            setShowUsernameDialog(true);
            setIsLoading(false);
            return;
          } else {
            // User already has a username and display_name, proceed to dashboard
            console.log("User has existing username:", profileData.username, "and display_name:", profileData.display_name);
            toast.success(t('sign_in_success'));
            navigate("/dashboard", { replace: true });
          }
        } else {
          console.log("No session found, redirecting to home");
          // If there's no session, redirect to the home page
          navigate("/", { replace: true });
        }
      } catch (error: any) {
        console.error("Error handling auth callback:", error);
        toast.error(error.message || t('sign_in_failed'));
        navigate("/", { replace: true });
      } finally {
        setIsLoading(false);
      }
    };

    handleAuthCallback();
  }, [navigate, searchParams, t]);

  const handleDialogComplete = () => {
    setShowUsernameDialog(false);
    navigate("/dashboard", { replace: true });
  };

  return (
    <>
      <div className="flex min-h-[400px] items-center justify-center">
        {isLoading ? (
          <div className="text-center">
            <h2 className="text-lg font-semibold">{t('completing_sign_in')}</h2>
            <p className="text-muted-foreground">{t('please_wait')}</p>
          </div>
        ) : null}
      </div>
      
      {userId && (
        <UsernameDialog 
          open={showUsernameDialog} 
          onOpenChange={setShowUsernameDialog} 
          onComplete={handleDialogComplete} 
        />
      )}
    </>
  );
}
